/* Reactions.tsx generated by @compiled/babel-plugin v0.36.1 */
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./Reactions.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { createAndFireSafe, createPickerButtonClickedEvent, createPickerCancelledEvent, createPickerMoreClickedEvent, createReactionsRenderedEvent, createReactionSelectionEvent, isSampled } from '../analytics';
import { SAMPLING_RATE_REACTIONS_RENDERED_EXP } from '../shared/constants';
import { messages } from '../shared/i18n';
import { ReactionStatus } from '../types';
import { ReactionDialogOpened, ReactionDialogSelectedReactionChanged } from '../ufo';
import { Reaction } from './Reaction';
import { ReactionsDialog } from './ReactionsDialog';
import { ReactionPicker as ReactionPickerOld } from './ReactionPicker';
import { ReactionPicker as ReactionPickerNew } from './ReactionPickerNew';
import { ReactionSummaryView } from './ReactionSummaryView';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
var ReactionPicker = componentWithFG('platform_editor_reactions_picker_fix', ReactionPickerNew, ReactionPickerOld);
var wrapperStyle = null;
var noFlexWrapStyles = null;
var noContainerPositionStyles = null;
var reactionPickerStyle = null;

/**
 * Set of all available UFO experiences relating to Reactions Dialog
 */
export var ufoExperiences = {
  /**
   * Experience when a reaction dialog is opened
   */
  openDialog: ReactionDialogOpened,
  /**
   * Experience when a reaction changed/fetched from inside the modal dialog
   */
  selectedReactionChangeInsideDialog: ReactionDialogSelectedReactionChanged
};

/**
 * Test id for wrapper Reactions div
 */
export var RENDER_REACTIONS_TESTID = 'render-reactions';
/**
 * Test id for the view all reacted user to trigger the dialog
 */
export var RENDER_VIEWALL_REACTED_USERS_DIALOG = 'viewall-reacted-users-dialog';

/**
 * Test id for summary view of reactions
 */
export var RENDER_REACTIONS_SUMMARY_TESTID = 'reaction-summary-view';
/**
 * Get content of the tooltip
 */
export function getTooltip(status, errorMessage, tooltipContent) {
  switch (status) {
    case ReactionStatus.error:
      return errorMessage || /*#__PURE__*/React.createElement(FormattedMessage, messages.unexpectedError);
    // When reaction is not available don't show any tooltip (e.g. Archive page in Confluence)
    case ReactionStatus.disabled:
      return null;
    case ReactionStatus.notLoaded:
    case ReactionStatus.loading:
      return /*#__PURE__*/React.createElement(FormattedMessage, messages.loadingReactions);
    case ReactionStatus.ready:
      return !!tooltipContent ? tooltipContent : /*#__PURE__*/React.createElement(FormattedMessage, messages.addReaction);
  }
}

/**
 * Renders list of reactions
 */
export var Reactions = /*#__PURE__*/React.memo(function (_ref) {
  var _ref$flash = _ref.flash,
    flash = _ref$flash === void 0 ? {} : _ref$flash,
    _ref$particleEffectBy = _ref.particleEffectByEmoji,
    particleEffectByEmoji = _ref$particleEffectBy === void 0 ? {} : _ref$particleEffectBy,
    status = _ref.status,
    errorMessage = _ref.errorMessage,
    loadReaction = _ref.loadReaction,
    quickReactionEmojis = _ref.quickReactionEmojis,
    pickerQuickReactionEmojiIds = _ref.pickerQuickReactionEmojiIds,
    _ref$getReactionDetai = _ref.getReactionDetails,
    getReactionDetails = _ref$getReactionDetai === void 0 ? function () {} : _ref$getReactionDetai,
    onSelection = _ref.onSelection,
    _ref$reactions = _ref.reactions,
    reactions = _ref$reactions === void 0 ? [] : _ref$reactions,
    emojiProvider = _ref.emojiProvider,
    allowAllEmojis = _ref.allowAllEmojis,
    onReactionClick = _ref.onReactionClick,
    allowUserDialog = _ref.allowUserDialog,
    _ref$onDialogOpenCall = _ref.onDialogOpenCallback,
    onDialogOpenCallback = _ref$onDialogOpenCall === void 0 ? function () {} : _ref$onDialogOpenCall,
    _ref$onDialogCloseCal = _ref.onDialogCloseCallback,
    onDialogCloseCallback = _ref$onDialogCloseCal === void 0 ? function () {} : _ref$onDialogCloseCal,
    _ref$onDialogSelectRe = _ref.onDialogSelectReactionCallback,
    onDialogSelectReactionCallback = _ref$onDialogSelectRe === void 0 ? function () {} : _ref$onDialogSelectRe,
    _ref$onDialogPageChan = _ref.onDialogPageChangeCallback,
    onDialogPageChangeCallback = _ref$onDialogPageChan === void 0 ? function () {} : _ref$onDialogPageChan,
    _ref$emojiPickerSize = _ref.emojiPickerSize,
    emojiPickerSize = _ref$emojiPickerSize === void 0 ? 'medium' : _ref$emojiPickerSize,
    _ref$miniMode = _ref.miniMode,
    miniMode = _ref$miniMode === void 0 ? false : _ref$miniMode,
    _ref$summaryViewEnabl = _ref.summaryViewEnabled,
    summaryViewEnabled = _ref$summaryViewEnabl === void 0 ? false : _ref$summaryViewEnabl,
    _ref$summaryViewThres = _ref.summaryViewThreshold,
    summaryViewThreshold = _ref$summaryViewThres === void 0 ? 3 : _ref$summaryViewThres,
    summaryViewPlacement = _ref.summaryViewPlacement,
    _ref$showOpaqueBackgr = _ref.showOpaqueBackground,
    showOpaqueBackground = _ref$showOpaqueBackgr === void 0 ? false : _ref$showOpaqueBackgr,
    _ref$subtleReactionsS = _ref.subtleReactionsSummaryAndPicker,
    subtleReactionsSummaryAndPicker = _ref$subtleReactionsS === void 0 ? false : _ref$subtleReactionsS,
    _ref$showAddReactionT = _ref.showAddReactionText,
    showAddReactionText = _ref$showAddReactionT === void 0 ? false : _ref$showAddReactionT,
    _ref$hideDefaultReact = _ref.hideDefaultReactions,
    hideDefaultReactions = _ref$hideDefaultReact === void 0 ? false : _ref$hideDefaultReact,
    ProfileCardWrapper = _ref.ProfileCardWrapper,
    _ref$onlyRenderPicker = _ref.onlyRenderPicker,
    onlyRenderPicker = _ref$onlyRenderPicker === void 0 ? false : _ref$onlyRenderPicker,
    _ref$isViewOnly = _ref.isViewOnly,
    isViewOnly = _ref$isViewOnly === void 0 ? false : _ref$isViewOnly,
    _ref$noWrap = _ref.noWrap,
    noWrap = _ref$noWrap === void 0 ? false : _ref$noWrap,
    _ref$noRelativeContai = _ref.noRelativeContainer,
    noRelativeContainer = _ref$noRelativeContai === void 0 ? false : _ref$noRelativeContai,
    showSubtleDefaultReactions = _ref.showSubtleDefaultReactions,
    reactionPickerTriggerTooltipContent = _ref.reactionPickerTriggerTooltipContent,
    reactionPickerTriggerIcon = _ref.reactionPickerTriggerIcon,
    _ref$allowSelectFromS = _ref.allowSelectFromSummaryView,
    allowSelectFromSummaryView = _ref$allowSelectFromS === void 0 ? false : _ref$allowSelectFromS,
    _ref$useButtonAlignme = _ref.useButtonAlignmentStyling,
    useButtonAlignmentStyling = _ref$useButtonAlignme === void 0 ? false : _ref$useButtonAlignme,
    reactionPickerTriggerText = _ref.reactionPickerTriggerText,
    _ref$hoverableSummary = _ref.hoverableSummaryView,
    hoverableSummaryView = _ref$hoverableSummary === void 0 ? false : _ref$hoverableSummary,
    _ref$isListItem = _ref.isListItem,
    isListItem = _ref$isListItem === void 0 ? false : _ref$isListItem,
    summaryGetOptimisticImageURL = _ref.summaryGetOptimisticImageURL,
    reactionPickerPlacement = _ref.reactionPickerPlacement,
    summaryButtonIconAfter = _ref.summaryButtonIconAfter,
    _ref$renderParticleEf = _ref.renderParticleEffectOnSummaryView,
    renderParticleEffectOnSummaryView = _ref$renderParticleEf === void 0 ? false : _ref$renderParticleEf;
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    selectedEmojiId = _useState2[0],
    setSelectedEmojiId = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    summaryViewParticleEffectEmojiId = _useState4[0],
    setSummaryViewParticleEffectEmojiId = _useState4[1];

  // if the reactions are empty, then set summaryViewParticleEffectEmojiId to null to clear the particle effect state
  useEffect(function () {
    if (reactions.length === 0) {
      setSummaryViewParticleEffectEmojiId(null);
    }
  }, [reactions]);
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var openTime = useRef();
  var renderTime = useRef();
  useEffect(function () {
    if (status === ReactionStatus.notLoaded) {
      loadReaction();
    }
  }, [status, loadReaction]);
  useEffect(function () {
    if (status !== ReactionStatus.ready) {
      renderTime.current = Date.now();
    } else {
      var isSSR = process.env.REACT_SSR;
      if (isSampled(SAMPLING_RATE_REACTIONS_RENDERED_EXP) && !isSSR) {
        var _renderTime$current;
        createAndFireSafe(createAnalyticsEvent, createReactionsRenderedEvent, (_renderTime$current = renderTime.current) !== null && _renderTime$current !== void 0 ? _renderTime$current : Date.now() //renderTime.current can be null during unit test cases
        );
      }
      renderTime.current = undefined;
    }
  }, [createAnalyticsEvent, status]);
  var handleReactionMouseEnter = useCallback(function (emojiId) {
    getReactionDetails(emojiId);
  }, [getReactionDetails]);
  var handleReactionFocused = useCallback(function (emojiId) {
    getReactionDetails(emojiId);
  }, [getReactionDetails]);
  var handlePickerOpen = useCallback(function () {
    openTime.current = Date.now();
    createAndFireSafe(createAnalyticsEvent, createPickerButtonClickedEvent, reactions.length);
  }, [createAnalyticsEvent, reactions]);
  var handleOnCancel = useCallback(function () {
    createAndFireSafe(createAnalyticsEvent, createPickerCancelledEvent, openTime.current);
    openTime.current = undefined;
  }, [createAnalyticsEvent]);
  var handleOnMore = useCallback(function () {
    createAndFireSafe(createAnalyticsEvent, createPickerMoreClickedEvent, openTime.current);
  }, [createAnalyticsEvent]);
  var handleSetSummaryViewParticleEffectEmojiId = useCallback(function (emojiId) {
    // Find if this emoji is already in existing reactions
    var existingReaction = reactions.find(function (reaction) {
      return reaction.emojiId === emojiId;
    });
    // Only set mostRecentlyClickedId if user hasn't reacted with this emoji yet
    if (!(existingReaction !== null && existingReaction !== void 0 && existingReaction.reacted)) {
      setSummaryViewParticleEffectEmojiId({
        id: emojiId,
        shortName: ''
      });
    } else {
      setSummaryViewParticleEffectEmojiId(null);
    }
  }, [reactions, setSummaryViewParticleEffectEmojiId]);
  var handleOnSelection = useCallback(function (emojiId, source) {
    createAndFireSafe(createAnalyticsEvent, createReactionSelectionEvent, source, emojiId, reactions.find(function (reaction) {
      return reaction.emojiId === emojiId;
    }), openTime.current);
    openTime.current = undefined;
    onSelection(emojiId);
    handleSetSummaryViewParticleEffectEmojiId(emojiId);
  }, [createAnalyticsEvent, onSelection, reactions, handleSetSummaryViewParticleEffectEmojiId]);
  var handleSummaryViewReactionClick = useCallback(function (emojiId, event) {
    onReactionClick(emojiId, event);
    handleSetSummaryViewParticleEffectEmojiId(emojiId);
  }, [onReactionClick, handleSetSummaryViewParticleEffectEmojiId]);
  var handleCloseReactionsDialog = function handleCloseReactionsDialog(e, analyticsEvent) {
    setSelectedEmojiId('');
    onDialogCloseCallback(e, analyticsEvent);
  };
  var handleSelectReactionInDialog = function handleSelectReactionInDialog(emojiId) {
    // ufo selected reaction inside the modal dialog
    ufoExperiences.selectedReactionChangeInsideDialog.start();
    handleReactionMouseEnter(emojiId);
    setSelectedEmojiId(emojiId);
    onDialogSelectReactionCallback(emojiId);

    // ufo selected reaction inside the modal dialog success
    ufoExperiences.selectedReactionChangeInsideDialog.success({
      metadata: {
        emojiId: emojiId,
        source: 'Reactions',
        reason: 'Selected Emoji changed'
      }
    });
  };
  var handlePaginationChange = useCallback(function (emojiId, currentPage, maxPages) {
    // fetch the latest active emoji from the new page
    getReactionDetails(emojiId);
    setSelectedEmojiId(emojiId);
    onDialogPageChangeCallback(emojiId, currentPage, maxPages);
  },
  // Exclude unstable getReactionDetails to avoid extra re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [setSelectedEmojiId]);

  /**
   * Get the reactions that we want to render are any reactions with a count greater than zero as well as any default emoji not already shown
   */
  var memorizedReactions = useMemo(function () {
    //
    /**
     * If reactions not empty, don't show quick reactions Pre defined emoji or if its empty and default reactions not hidden => return the current list of reactions
     */
    if (reactions.length === 0 && hideDefaultReactions || reactions.length > 0 || !quickReactionEmojis) {
      return reactions;
    }

    // add any missing default reactions
    var ari = quickReactionEmojis.ari,
      containerAri = quickReactionEmojis.containerAri,
      emojiIds = quickReactionEmojis.emojiIds;
    var items = emojiIds.filter(function (emojiId) {
      return !reactions.some(function (reaction) {
        return reaction.emojiId === emojiId;
      });
    }).map(function (emojiId) {
      return {
        ari: ari,
        containerAri: containerAri,
        emojiId: emojiId,
        count: 0,
        reacted: false
      };
    });
    return reactions.concat(items);
  }, [quickReactionEmojis, reactions, hideDefaultReactions]);
  var shouldShowSummaryView = summaryViewEnabled && memorizedReactions.length >= summaryViewThreshold && reactions.length > 0;

  // criteria to show Reactions Dialog
  var hasEmojiWithFivePlusReactions = reactions.some(function (reaction) {
    return reaction.count >= 5;
  });
  var sortedReactions = useMemo(function () {
    return _toConsumableArray(memorizedReactions).sort(function (a, b) {
      return (b === null || b === void 0 ? void 0 : b.count) - (a === null || a === void 0 ? void 0 : a.count);
    });
  }, [memorizedReactions]);

  /**
   * event handler to open dialog with selected reaction
   * @param emojiId initial emoji id to load dialog with
   */
  var _handleOpenReactionsDialog = function handleOpenReactionsDialog() {
    var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref2$emojiId = _ref2.emojiId,
      emojiId = _ref2$emojiId === void 0 ? sortedReactions[0].emojiId : _ref2$emojiId,
      _ref2$source = _ref2.source,
      source = _ref2$source === void 0 ? 'endOfPageReactions' : _ref2$source;
    // ufo start opening reaction dialog
    ufoExperiences.openDialog.start();
    getReactionDetails(emojiId);
    setSelectedEmojiId(emojiId);
    onDialogOpenCallback(emojiId, source);

    // ufo opening reaction dialog success
    ufoExperiences.openDialog.success({
      metadata: {
        emojiId: emojiId,
        source: 'Reactions',
        reason: 'Opening Reactions Dialog successfully'
      }
    });
  };
  return /*#__PURE__*/React.createElement(UFOSegment, {
    name: "reactions"
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": RENDER_REACTIONS_TESTID,
    className: ax(["_2rkoo7ao _1e0c1txw _1n261g80 _kqswh2mm _4cvr1h6o _19pkr5cr _13vtidpf", noWrap && "_1n261q9c", noRelativeContainer && "_kqswpfqs"])
  }, !onlyRenderPicker && (shouldShowSummaryView ? /*#__PURE__*/React.createElement("div", {
    "data-testid": RENDER_REACTIONS_SUMMARY_TESTID
  }, /*#__PURE__*/React.createElement(ReactionSummaryView, {
    reactions: sortedReactions,
    emojiProvider: emojiProvider,
    flash: flash,
    particleEffectByEmoji: particleEffectByEmoji,
    onSelection: handleOnSelection,
    onReactionClick: handleSummaryViewReactionClick,
    onReactionFocused: handleReactionFocused,
    onReactionMouseEnter: handleReactionMouseEnter,
    placement: summaryViewPlacement,
    showOpaqueBackground: showOpaqueBackground,
    subtleReactionsSummaryAndPicker: subtleReactionsSummaryAndPicker,
    handleOpenReactionsDialog: function handleOpenReactionsDialog() {
      return _handleOpenReactionsDialog({
        source: 'summaryView'
      });
    },
    allowUserDialog: allowUserDialog && hasEmojiWithFivePlusReactions,
    isViewOnly: isViewOnly,
    allowSelectFromSummaryView: allowSelectFromSummaryView,
    disabled: status !== ReactionStatus.ready,
    reactionPickerTriggerIcon: reactionPickerTriggerIcon,
    tooltipContent: getTooltip(status, errorMessage, reactionPickerTriggerTooltipContent),
    emojiPickerSize: emojiPickerSize,
    onOpen: handlePickerOpen,
    useButtonAlignmentStyling: useButtonAlignmentStyling,
    reactionPickerTriggerText: reactionPickerTriggerText,
    hoverableSummaryView: hoverableSummaryView,
    summaryGetOptimisticImageURL: summaryGetOptimisticImageURL,
    summaryButtonIconAfter: summaryButtonIconAfter,
    summaryViewParticleEffectEmojiId: renderParticleEffectOnSummaryView ? summaryViewParticleEffectEmojiId : null
  })) : memorizedReactions.map(function (reaction) {
    return /*#__PURE__*/React.createElement(Reaction, {
      key: reaction.emojiId,
      reaction: reaction,
      emojiProvider: emojiProvider,
      onClick: onReactionClick,
      onMouseEnter: handleReactionMouseEnter,
      onFocused: handleReactionFocused,
      flash: flash[reaction.emojiId],
      showParticleEffect: particleEffectByEmoji[reaction.emojiId],
      showOpaqueBackground: showOpaqueBackground,
      allowUserDialog: allowUserDialog && hasEmojiWithFivePlusReactions,
      handleOpenReactionsDialog: _handleOpenReactionsDialog,
      isViewOnly: isViewOnly,
      showSubtleStyle: showSubtleDefaultReactions && reactions.length === 0
    });
  })), isViewOnly || !onlyRenderPicker && shouldShowSummaryView && allowSelectFromSummaryView ? null : /*#__PURE__*/React.createElement(ReactionPicker, {
    emojiProvider: emojiProvider,
    allowAllEmojis: allowAllEmojis,
    pickerQuickReactionEmojiIds: pickerQuickReactionEmojiIds,
    disabled: status !== ReactionStatus.ready,
    onSelection: handleOnSelection,
    onOpen: handlePickerOpen,
    onCancel: handleOnCancel,
    onShowMore: handleOnMore,
    tooltipContent: hoverableSummaryView ? null : getTooltip(status, errorMessage, reactionPickerTriggerTooltipContent),
    emojiPickerSize: emojiPickerSize,
    miniMode: miniMode,
    showOpaqueBackground: showOpaqueBackground,
    showAddReactionText: showAddReactionText,
    subtleReactionsSummaryAndPicker: subtleReactionsSummaryAndPicker,
    reactionPickerTriggerIcon: reactionPickerTriggerIcon,
    reactionPickerTriggerText: reactionPickerTriggerText,
    isListItem: isListItem,
    hoverableReactionPicker: hoverableSummaryView,
    hoverableReactionPickerDelay: hoverableSummaryView ? 300 : 0,
    reactionPickerPlacement: reactionPickerPlacement,
    className: ax(["_1e0c1o8l _19pk1b66"])
  }), /*#__PURE__*/React.createElement(ModalTransition, null, !!selectedEmojiId && /*#__PURE__*/React.createElement(UFOSegment, {
    name: "reactions-dialog"
  }, /*#__PURE__*/React.createElement(ReactionsDialog, {
    selectedEmojiId: selectedEmojiId,
    reactions: sortedReactions,
    emojiProvider: emojiProvider,
    handleCloseReactionsDialog: handleCloseReactionsDialog,
    handleSelectReaction: handleSelectReactionInDialog,
    handlePaginationChange: handlePaginationChange,
    ProfileCardWrapper: ProfileCardWrapper
  })))));
});